import { Undo2, MessageSquareReply, ExternalLink } from "lucide-react";

export const STATUS_ORDER = ["open", "in_progress", "resolved", "dismissed"];
export const STATUS_LABEL = { open: "Open", in_progress: "In Progress", resolved: "Resolved", dismissed: "Dismissed" };
const TONE = { open: "text-amber-300 border-amber-500/40 bg-amber-500/10", in_progress: "text-sky-300 border-sky-500/40 bg-sky-500/10", resolved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", dismissed: "text-slate-500 border-slate-700 bg-slate-800/40" };

export function StatusPill({ f }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono ${TONE[f.status] || TONE.open}`} data-testid={`finding-status-pill-${f.id}`}>
      {STATUS_LABEL[f.status] || f.status}{f.status === "in_progress" && f.rfi_item && <span className="text-sky-400/80">· Included in {f.rfi_item.rfi_no}</span>}
    </span>
  );
}

export function StatusMeta({ f }) {
  const who = f.status === "resolved" ? [f.resolved_by_name, f.resolved_at] : f.status === "dismissed" ? [f.dismissed_by_name, f.dismissed_at] : null;
  if (!who) return null;
  return <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`finding-status-meta-${f.id}`}>{STATUS_LABEL[f.status]} by {who[0] || "—"} · {who[1] ? new Date(who[1]).toLocaleDateString() : ""}</div>;
}

export function RfiResponseFlag({ f, onResolve, canEdit }) {
  if (!f.rfi_response_pending) return null;
  return (
    <div className="mt-1 inline-flex flex-wrap items-center gap-2 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-200" title={f.rfi_response} data-testid={`finding-rfi-response-${f.id}`}>
      <MessageSquareReply className="w-3.5 h-3.5" /> RFI Response Received — Review and Resolve
      {canEdit && <button onClick={(e) => { e.stopPropagation(); onResolve(f); }} className="border border-emerald-500/60 hover:bg-emerald-500/20 rounded px-1.5 py-0.5" data-testid={`finding-resolve-from-rfi-${f.id}`}>Mark Resolved</button>}
    </div>
  );
}

export function RfiLink({ f, onOpen }) {
  if (!f.rfi_item) return null;
  return (
    <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200 underline decoration-dotted" title={`Open RFI package ${f.rfi_item.number} (${f.rfi_item.status})`} data-testid={`finding-rfi-link-${f.id}`}>
      <ExternalLink className="w-3 h-3" /> {f.rfi_item.number} · {f.rfi_item.rfi_no}
    </button>
  );
}

export function ReopenButton({ f, onReopen }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onReopen(f); }} className="inline-flex items-center gap-1 text-[10px] border border-slate-600 hover:border-amber-400 hover:text-amber-300 rounded px-1.5 py-0.5 text-slate-300" data-testid={`reopen-finding-${f.id}`}>
      <Undo2 className="w-3 h-3" /> {f.status === "dismissed" ? "Restore" : "Reopen"}
    </button>
  );
}

export function StatusSummary({ summary, active, onPick }) {
  if (!summary) return null;
  const chip = (key, label, n, tone) => (
    <button key={key} onClick={() => onPick(key)} className={`rounded-md border px-2.5 py-1 text-xs font-mono transition-colors ${active === key ? "border-amber-500 text-amber-300 bg-amber-500/10" : "border-slate-800 text-slate-400 hover:border-slate-600"} ${tone || ""}`} data-testid={`status-chip-${key || "all"}`}>
      {label} <b className="text-slate-100">{n}</b>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="findings-status-summary">
      {chip("", "All", summary.open + summary.in_progress + summary.resolved + summary.dismissed)}
      {chip("active", "Active", summary.active)}
      <span className="text-slate-700">|</span>
      {chip("open", "Open", summary.open)}
      {chip("in_progress", "In Progress", summary.in_progress)}
      {chip("resolved", "Resolved", summary.resolved)}
      {chip("dismissed", "Dismissed", summary.dismissed)}
      {summary.rfi_response_pending > 0 && <span className="ml-2 text-[11px] text-emerald-300" data-testid="rfi-responses-pending">{summary.rfi_response_pending} RFI response{summary.rfi_response_pending > 1 ? "s" : ""} to review</span>}
    </div>
  );
}

export function GroupHeader({ status, count, collapsed, onToggle }) {
  return (
    <tr className="bg-slate-950/70" data-testid={`group-header-${status}`}>
      <td colSpan={8} className="py-1.5 px-3 text-[11px] font-mono uppercase tracking-wider text-slate-400">
        <button onClick={onToggle} className="inline-flex items-center gap-2 hover:text-slate-200" data-testid={`group-toggle-${status}`}>
          <span>{collapsed ? "▸" : "▾"}</span>{STATUS_LABEL[status]} <span className="text-slate-500">({count})</span>
          {status === "dismissed" && <span className="normal-case tracking-normal text-slate-600">— no action required; history preserved</span>}
          {status === "resolved" && <span className="normal-case tracking-normal text-slate-600">— addressed; kept for project history</span>}
        </button>
      </td>
    </tr>
  );
}
